import { ChangeDetectorRef, Component } from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ToastrService } from '@openng/ngx-toastr';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, firstValueFrom, of, Subject, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { CommonService } from '../services/common.service';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CommissionCalculationStepsComponent } from '../components/shared/commission-calculation-steps-dialog/commission-calculation-steps-dialog.component';
import { ContributorCommissionRatePlanComponent } from '../components/configuration/contributor/contributor-commission-rate/contributor-commission-rate.component';

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [NgxSpinnerModule, MatToolbarModule, MatButtonModule, MatMenuModule, MatIconModule, RouterModule, CommonModule, HttpClientModule],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss'
})
export class NavigationComponent {
  private isInitialized = false;

  logo = `${environment.logoUrl}${environment.headerAltisLogo}`;
  currentRoute: string[] = [];
  profile?: MicrosoftGraph.User;
  hasPhoto: boolean = false;
  profilePhoto: any = '';
  loginDisplay = false;
  userRole: string = '';
  userRoleRights: string[] = [];
  isUserLogin: boolean = false;
  isUserRoleLoaded: boolean = false;
  doesUserHaveRole: boolean = true;

  constructor(
    private router: Router,
    private client: HttpClient,
    private toastr: ToastrService,
    private authService: MsalService,
    private spinner: NgxSpinnerService,
    private commonService: CommonService,
    private msalBroadcastService: MsalBroadcastService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
  }
  openContributorCommissionRateDialog(): void {

    this.commonService.getLoggedInUserContributorDetails().subscribe({
      next: (user) => {

        this.dialog.open(ContributorCommissionRatePlanComponent, {
          ...this.commonService.dialog_xl,
          data: {
            user: user
          }
        });
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.commonService.handleError(error);
        this.cdr.detectChanges();

      }
    });
  }

  openContractCalculationDialog(): void {

    this.dialog.open(CommissionCalculationStepsComponent, {
      width: 'auto',
      height: '80vh'
    });


  }

  protected ngUnsubscribe = new Subject<void>();

  async ngOnInit(): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.authService.instance.initialize().then(() => {
      // MSAL is now ready, safe to use MSAL APIs here
      let activeAccount = this.authService.instance.getActiveAccount();

      // Restore active account from cached accounts after reloads.
      if (!activeAccount) {
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
          activeAccount = accounts[0];
          this.authService.instance.setActiveAccount(activeAccount);
        }
      }

      if (activeAccount) {
        this.getProfile(environment.apiConfig.uri);
        this.isUserLogin = true;
        this.cdr.detectChanges();
      }
      else {
        this.hadleRedirect();
        this.cdr.detectChanges();
      };
    });


    //Need to initialize the MSAL instance
    // await this.authService.initialize();

    // Reset interaction status when no interaction is in progress
    this.msalBroadcastService.inProgress$.pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        this.setLoginDisplay();
        this.cdr.detectChanges();
      });

  }

  async hadleRedirect() {
    this.authService.handleRedirectObservable().subscribe(async (res) => {
      if (res?.account) {
        this.authService.instance.setActiveAccount(res.account);
        this.isUserLogin = true;
        this.setLoginDisplay();
        await this.getProfile(environment.apiConfig.uri);
        this.cdr.detectChanges();
      }
    });
  }

  async loginPopup() {
    this.authService.loginPopup().pipe(take(1)).subscribe({
      next: (response: AuthenticationResult) => {
        this.authService.instance.setActiveAccount(response.account);
        this.isUserLogin = true;
        this.cdr.detectChanges();

        // Wait for interaction to settle, then set state
        this.msalBroadcastService.inProgress$
          .pipe(
            filter(status => status === InteractionStatus.None),
            take(1)
          )
          .subscribe(() => {
            this.setLoginDisplay();
            this.getProfile(environment.apiConfig.uri);
            this.cdr.detectChanges();
          });
      },
      error: (err) => {
        if (err.errorCode === 'state_mismatch') {
          this.toastr.error('State mismatch. Please try logging in again.', 'Login Error');
          this.cdr.detectChanges();
          setTimeout(() => {
          }, 2000);
        }
      }
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    this.cdr.detectChanges();
  }

  async getProfile(url: string) {

    try {
      // Use `firstValueFrom` to await the observable
      this.profile = await firstValueFrom(this.client.get<any>(url));

      await this.getUserGroups();
      this.commonService.setProfile(this.profile);
      this.cdr.detectChanges();

      // Fetch photo
      this.client
        .get(`${environment.msGraph_URL}/me/photo/$value`, { responseType: 'blob' })
        .pipe(
          catchError((error) => {
            console.error('Error fetching photo:', error);
            this.hasPhoto = false;
            this.profilePhoto = `${this.profile?.givenName?.charAt(0)}${this.profile?.surname?.charAt(0)}`;
            this.cdr.detectChanges();
            return of(null); // allow stream to continue
          })
        )
        .subscribe((photo) => {
          if (photo) {
            this.hasPhoto = true;
            const reader = new FileReader();
            reader.readAsDataURL(photo);
            reader.onloadend = () => {
              this.profilePhoto = reader.result;
              this.cdr.detectChanges();
            };
          }
        });

    } catch (error) {
      console.error('Error fetching profile:', error);
      this.cdr.detectChanges();

    }
  }

  getUserGroups() {
    return new Promise((resolve) => {
      this.commonService.getUserGroups().subscribe({
        next: (groups: any) => {

          this.userRole = groups.roles;

          this.commonService.setRoles(groups.roleRights);
          this.userRoleRights = groups.roleRights;

          this.isUserRoleLoaded = true;
          this.cdr.detectChanges();
          resolve(true);
        },
        error: (error: any) => {
          this.doesUserHaveRole = false;
          this.commonService.handleError(error);
          this.cdr.detectChanges();

          resolve(true);
        }

      });
    });
  }

  logout() {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.isInitialized = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getLoggedInUserContributorDetails() {
    return this.commonService.getLoggedInUserContributorDetails().subscribe({
      next: (data) => {
        return data;
      },
      error: (error) => {
        return null;
      }
    });
  }
}